/**
 * Part del State necessari per la consulta de llicències
 * Component: LlicUrbState.ts
 * Llenguatge: TypeScript
 * Autors: Xavier Naval
 * Versió: 1.0
 * Data: 26/04/2022
 * (c) Ajuntament de Manresa - abr 2022 - Departament de Sistemes d'Informació
 */

import { observable, action } from 'mobx';
import proj4 from 'proj4';
import OlLayerImage from 'ol/layer/Image';
import OlImageSource from 'ol/source/Image';
import OlLayerTile from 'ol/layer/Tile';
import OlTileSource from 'ol/source/Tile';
import OlOverlay from 'ol/Overlay';
import OlView from 'ol/View';
import OlMap from 'ol/Map';
import OlScaleLine from 'ol/control/ScaleLine';
import { Options } from 'ol/source/WMTS';
import { Extent, getCenter } from 'ol/extent';
import { Coordinate } from 'ol/coordinate';
import { IPropsMapa } from '../interfaces/IPropsMapa';
import { IFinCod, IRefCad } from '../interfaces/IRefCadAdr';
import Util from '../config/util';
import BaseState from './BaseState';
import { ILayer, ILayerGroup } from '../interfaces/ILayer';
// import Geolocation from 'ol/Geolocation';
 
// Zoom global mínim (lo més allunyat)
const minZ = 12;

// Zoom global màxim (lo més aprop)
const maxZ = 20;

export default class LlicState extends BaseState {
    // Constructor
    constructor(props: IPropsMapa) {
        super(props, 'llic');
    }

    // Element HTML del contingut de l'overlay
    private popupContent: HTMLDivElement = document.createElement('div');

    // Element HTML de l'Overlay
    private popupElement: HTMLDivElement = this.createPopup(); // document.createElement('div');

    // El Overlay
    private popupOverlay = new OlOverlay({
        element: this.popupElement,
        autoPan: { animation: { duration: 250 } }
    });

    // Capa base inicial
    private iBase = '';

    // Capes inicialment activades
    private iLayers: string[];

    // Número d'adreça inicial
    private iNumCod = 0;

    // Referència cadastral inicial
    private iRefCad: string;

    // Codi de carrer inicial
    private iCarCod: string;

    // Tipus de via inicial
    private iTipCarr: string;

    // Nom de carrer inicial
    private iNomCarr: string;

    // Número postal inicial
    private iNumero: string;

    // Posició inicial
    private iPosX = 0
    private iPosY = 0
    private iPosZoom = 0

    // La capa de texts d'edificació
    public txconsLayer?: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>;

    // La capa de parcel·les amb llicència
    public pllicLayer: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>;
 
    // BaseLayer actual
    @observable actBaseLayer: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>;

    // Diu si es veu el checkbox de la capa de texts d'edificació
    @observable showTxCons = false;

    // Diu si la capa de texts d'edificació estava visible
    @observable txConsVisible = false;

    // Que fem al clicar un punt del mapa (1 -> Triem parcel·la, 2 -> Triem punt)
    @observable identMode = 0;

    // Filtre per tipus de procediment
    @observable filterProc = 'LLI.OMA';

    // Filtre per any de registre de procediment
    @observable filterAny = '2025';

    // Filtre per procediments oberts
    @observable filterOberts = true;

    // Filtre per procediments oberts
    @observable filterTancats = false;

    // Torna el zoom inicial
    private getInitialZoom() : number {
        if (this.mapConfig.initialZoom) {
            return this.mapConfig.initialZoom;
        } else{
            return this.getMinZoom(null);
        }
    }

    // Torna el zoom mínim
    private getMinZoom(ly: ILayer) : number {
        let mz: number = minZ;
        if (this.mapConfig.minZoom) {
            mz = this.mapConfig.minZoom;
        }
        if (ly && ly.minZoom) {
            mz = Math.max(mz, ly.minZoom);
        }
        return mz;
    }

    // Torna el zoom màxim
    private getMaxZoom(ly: ILayer) : number {
        let mz: number = maxZ;
        if (this.mapConfig.maxZoom) {
            mz = this.mapConfig.maxZoom;
        }
        if (ly && ly.maxZoom) {
            mz = Math.min(mz, ly.maxZoom);
        }
        return mz;
    }

    // Crea el element del Popup
    private createPopup(): HTMLDivElement {
        const ppp: HTMLDivElement = document.createElement('div');
        ppp.className = 'pllic-popup';
        ppp.id = 'popup';
        const pppclsr:HTMLAnchorElement = document.createElement('a');
        pppclsr.className = 'pllic-popup-closer';
        pppclsr.id = 'popupcloser';
        pppclsr.href = '#';
        pppclsr.onclick = () => {
            this.popupOverlay.setPosition(undefined);
            document.getElementById('popupcloser').blur();
            return false;
        };
        ppp.appendChild(pppclsr);
        this.popupContent.id = 'popupcontent';
        this.popupContent.className = 'pllic-popup-content';
        ppp.appendChild(this.popupContent);
        return ppp;
    }

    // Diu si una capa és una capa inicial
    private isILayer(ly: string): boolean {
        if (this.iLayers && this.iLayers.length > 0) {
            const lly = ly.toLowerCase();
            if (this.iLayers.filter(item => {
                const litem = item.toLowerCase();
                return lly === litem;
            }).length > 0) {
                return true;
            }
        }
        return false;
    }

    // Afegeix una capa de referencia
    private addRefLayer(rl: ILayer | ILayerGroup) {
        let olLy: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>;
        if (rl.type === 'wms') {
            const rll = rl as ILayer;
            const visible = this.isILayer(rll.layers) || this.isILayer(rll.id);
            olLy = this.createWMSLayer(rll.id, rll.layers, rll.url, this.getLayerExtraParms(rll), rll.layers, rll.singleTile, true, visible, rll.opacity, rll.info, this.getMinZoom(rll), this.getMaxZoom(rll));
            this.refLayers.push(olLy);
            this.actRefLayers.push(visible);
        } else if (rl.type === 'wmts') {
            const rll = rl as ILayer;
            if (rll.cap) {
                const visible = this.isILayer(rll.layers) || this.isILayer(rll.id);
                olLy = this.createWMTSLayer(rll.id, rll.name, rll.cap as Options, visible, rll.opacity, this.getMinZoom(rll), this.getMaxZoom(rll));
                this.refLayers.push(olLy);
                this.actRefLayers.push(visible);
            }
        } else if (rl.type === 'grp') { // Capes de referència dins d'un grup
            (rl as ILayerGroup).layers.forEach((rl2) => { this.addRefLayer(rl2); });
        }
    }

    // Processem els paràmetres de la URL
    private getURLParameters() {
        const usp: URLSearchParams = new URLSearchParams(window.location.search);
        // Mirem si ens han passat Base
        this.iBase = usp.get('Base');
        // Mirem si ens han passat Layers
        const lys = usp.get('Layers');
        if (lys) {
            this.iLayers = usp.get('Layers').split(',');
        }
        // Mirem si ens han passat un NumCod
        const nc = usp.get('NumCod');
        if (nc) {
            this.iNumCod = parseInt(nc);
        }
        // Mirem si ens han passat una referència cadastral
        this.iRefCad = usp.get('RefCad');
        if (!this.iRefCad) {
            this.iRefCad = usp.get('RefPar');
            if (!this.iRefCad) {
                this.iRefCad = usp.get('Ref1') + usp.get('Ref2');
            }
        }
        // Eliminem les referècies de rústica de 7 dígits o les refrències de menys de 7 dígits
        if (this.iRefCad && (this.iRefCad === '08112A0' || this.iRefCad.length < 7)) {
            this.iRefCad = '';
        }
        // Mirem si ens han passat una adreça
        this.iCarCod = usp.get('CarCod');
        this.iTipCarr = usp.get('TipCarr');
        this.iNomCarr = usp.get('NomCarr');
        if (this.iNomCarr) {
            this.iNomCarr = this.iNomCarr.replace(/_/g, ' ');
        }
        this.iNumero = usp.get('Numero');
        // Mirem si ens han passat una posició i nivell de zoom
        const iPos: string = usp.get('Pos');
        if (iPos) {
            const iposA: Array<string> = iPos.split(';');
            if (iposA.length >= 2) {
                this.iPosX = parseFloat(iposA[0]);
                this.iPosY = parseFloat(iposA[1]);
                if (iposA.length > 2) {
                    this.iPosZoom = parseInt(iposA[2]);
                }
            }
        }
    }

    // Posa el contingut del Popup d'informació d'un element
    private setPoupContent(c: Coordinate, content: string) {
        // FitxUrbPopUp.setPoupContent(c, content, this.popupContent, this.popupOverlay, `${this._props.appState.entorn}`);
        let txElem = '';
        let flag = false;
        let txHtml = '';
        const pos = { pos: 0 };
        let oldPos = 0;

        // content = content.replace(/\s+/g, ' ').trim(); // Traiem els retorns de carro, tabuladors, ...
        // Construim el títol
        const txHtmlTit = '<h2><b>Llicències de ' + this.finRefCad1 + ' ' + this.finRefCad2 + '</b></h2><hr/>';
        // Construim el cos
        txHtml = '<table border="1"><tr><th>Procediment</th><th>Tràmit</th><th>Data</th><th>Extracte</th>';

        oldPos = pos.pos;
        txElem = Util.getBetweenI("<proc>", "</proc>", content, pos);
        flag = false;
        while (txElem > "") {
            flag = true;
            const clasCod = Util.getBetweenI("<CLASCOD>", "</CLASCOD>", txElem);
            const clasNum = Util.getBetweenI("<CLASNUM>", "</CLASNUM>", txElem);
            txHtml += "</tr><tr><td><a href='#' onclick='window.open(\"http://wfserver/registreProcediments/registre/" + clasCod+ "/" + clasNum + "\")'><span style='color: blue; text-decoration:underline;'>" + clasCod + "&nbsp;" + clasNum + "</span></a></td>";
            let tdesc = Util.getBetweenI("<TRAMITDESC>", "</TRAMITDESC>", txElem);
            tdesc = Util.replaceAll(tdesc, '"', ' ');
            let tdesctt = '';
            if (tdesc.length > 25) {
                tdesctt = tdesc;
                tdesc = tdesc.substring(0, 21) + '...';
            }
            txHtml += "<td><span title= \"" + Util.encodeHTML(Util.escapeXML(tdesctt)) + "\">" + Util.encodeHTML(Util.escapeXML(tdesc)) + "</span></td>";
            const regdata = Util.getBetweenI("<REGDATA>", "</REGDATA>", txElem);
            txHtml += "<td><span>" + regdata + "</span></td>";
            let extracte = Util.getBetweenI("<EXTRACTE>", "</EXTRACTE>", txElem);
            extracte = Util.replaceAll(extracte, '"', ' ');
            let extractett = '';
            if (extracte.length > 50) {
                extractett = extracte;
                extracte = extracte.substring(0, 46) + '...';
            }
            txHtml += "<td><span title= \"" + Util.encodeHTML(Util.escapeXML(extractett)) + "\">" + Util.encodeHTML(Util.escapeXML(extracte)) + "</span></td>";
            oldPos = pos.pos;
            txElem = Util.getBetweenI("<proc>", "</proc>", content, pos);
        }
        pos.pos = oldPos;
        txHtml += '</tr></table>';
        if (!flag) {
            txHtml += "<br /><h2>No hi ha cap tràmit en curs</h2>";
        }   

        // posem el contingut al PopUp
        this.popupContent.innerHTML = txHtmlTit + '<div style="max-height: 400px; overflow-y: auto">' + txHtml + '</div>';
        this.popupOverlay.setPosition(c);
    } 

    // Busca una posició per nom de carrer
    private setFIDFromCarrer = (carCod: string, carSig: string, carDesc: string, numNum: string) => {
        const xml = '<?xml version="1.0" encoding="utf-8"?><soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/"><soap:Body><GetAdrecaList xmlns="http://www.ajmanresa.cat/GuiaWS"><pXML>' + 
                    Util.encodeHTML('<WSParam><Session>' + this.ses + '</Session><Usuari>' + this.usr + '</Usuari>' + 
                                    (carCod ? '<CarCod>' + carCod +  '</CarCod>' 
                                            : (carSig ? '<CarSig>' + carSig + '</CarSig>' : '') + '<CarDesc>' + carDesc + '</CarDesc>') + 
                                    '<NumNum>' + numNum + '</NumNum></WSParam>') + '</pXML></GetAdrecaList></soap:Body></soap:Envelope>';
        this.req.postRequestRaw(this.mapConfig.wsGuia, xml, { headers: {'Content-Type': 'text/xml; charset=utf-8'} }).then((result) => {
            const adrList: string = Util.getBetween("<GetAdrecaListResult>", "</", result as string);
            // Retorna el següent : "Adreca 1#403557.63#4621691.05#3275;Adreca 2#402546.23#4620345.09#1843"
            if (adrList) {
                const adrArray: string[] = adrList.split(";"); // Separa la cadena per ;
                if (adrArray.length > 0) {
                    const adreca: string[] = adrArray[0].split("#"); // Seapar la cadena per #
                    this.setFIDFromNumCod(parseInt(adreca[3]));
                }
            }
        }).catch(() => { /* No fa res*/ });                              
    }

    // Posa el zoom de la posició passada per paràmetre (zoom entre 14 i 20)
    private setInitialPos(posX: number, posY: number, posZoom: number) {
        const ext: Extent = [posX - 1, posY - 1, posX + 1, posY + 1];
        this.olview.fit(ext);
        if (posZoom >= 14 && posZoom <= 20) {
            this.olview.setZoom(posZoom);
        }
    }
    
    // Posa la posició del dispositiu
    public setActualLocation() {
        navigator.geolocation.getCurrentPosition((position) => {
            if (position) {
                // alert("Lon:" + position.coords.longitude + " Lat:" + position.coords.latitude);
                const lonLat = [position.coords.longitude, position.coords.latitude];
                const coord = proj4('EPSG:4326', this.olproj.getCode()).forward(lonLat);
                // alert("X:" + coord[0] + " Y:" + coord[1]);
                const ext: Extent = [coord[0] - 1, coord[1] - 1, coord[0] + 1, coord[1] + 1];
                this.olview.fit(ext);
                // this.position = coord;
                // this.setMarker();
                if (this.markerSource) {
                    this.markerSource.clear(true);
                }
            }
        });
        // const geolocation = new OlGeolocation({
        //     projection: this.olview.getProjection()
        // });
        // // geolocation.on('change:position', () => {
        // //     const coord = geolocation.getPosition();
        // //     if (coord) {
        // //         const ext: Extent = [coord[0] - 1, coord[1] - 1, coord[0] + 1, coord[1] + 1];
        // //         this.olview.fit(ext);
        // //         this.position = coord;
        // //         this.setMarker();
        // //     }
        // // });
        // const coord: Coordinate = geolocation.getPosition();
        // if (coord) {
        //     const ext: Extent = [coord[0] - 1, coord[1] - 1, coord[0] + 1, coord[1] + 1];
        //     this.olview.fit(ext);
        //     this.position = coord;
        //     this.setMarker();
        // }
    }

    // Construim el mapa i hi afegim la llista de capes i el controls estàndard
    // Tal com funciona ara el control MyLayerSwitcher, només afegim al mapa una capa base
    public createMap() {
        this.getURLParameters(); // llegim els paràmetres de la URL
        let showNoms = false;
        this.mapConfig.baseLayers.forEach((bl) => { // Capes base
            const act: boolean = this.iBase && (bl.id.toLowerCase() === this.iBase.toLowerCase() || bl.layers.toLowerCase() === this.iBase.toLowerCase());
            let olLy: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>;
            if (bl.type === 'wms') {
                olLy = this.createWMSLayer(bl.id, bl.layers, bl.url, this.getLayerExtraParms(bl), bl.layers, bl.singleTile, true, act, 1.0, false, this.getMinZoom(bl), this.getMaxZoom(bl)); // Posem com a visible la passada per paràmetres
                this.baseLayers.push(olLy);
            } else if (bl.type === 'wmts' && bl.cap) {
                olLy = this.createWMTSLayer(bl.id, bl.name, bl.cap as Options, act, 1.0, this.getMinZoom(bl), this.getMaxZoom(bl)); // Posem com a visible la 1a capa base
                this.baseLayers.push(olLy);
            }
            if (act) {
                showNoms = bl.noms;
                this.actBaseLayer = olLy;
            }
        });
        // NO hem trobat la capa base
        if (!this.actBaseLayer) {
            showNoms = this.mapConfig.baseLayers[0].noms;
            this.baseLayers[0].setVisible(true);
            this.actBaseLayer = this.baseLayers[0];
        }
        this.olview = new OlView({
            projection: this.olproj, // Projecció ETRS89 UTM 31N
            center: getCenter(this.mapConfig.initialExtent),
            extent: Util.isEmptyExtent(this.mapConfig.maxExtent) ? this.mapConfig.initialExtent : this.mapConfig.maxExtent,
            zoom: this.getInitialZoom(),
            minZoom: this.getMinZoom(null),
            maxZoom: this.getMaxZoom(null)
        });
        this.mapConfig.refLayers.forEach((rl) => { // Capes de referència
            this.addRefLayer(rl);
        });

        // Tal com funciona ara el control MyLayerSwitcher, només afegim al mapa una capa base
        this.olmap = new OlMap({
            layers: [this.actBaseLayer].concat(this.refLayers), // this.baseLayers.concat(this.refLayers),
            view: this.olview
        });
        this.olmap.on('postcompose', this.olmap.updateSize);
        this.olmap.on('singleclick', this.processMapClick); // Funció per identificar un element
        if (this.mapConfig.measureTool) {
            this.olmap.on('pointermove', this.pointerMove);
        }

        // Per cancel·lar les comandes amb el botó dret.
        this.olmap.getViewport().addEventListener('contextmenu', this.cancelMapInteraction);

        if (this.mapConfig.scaleBar) { // Afegim el control escala si cal
            this.olmap.addControl(new OlScaleLine({
                units: 'metric',
                bar: false,
                steps: 4,
                text: true,
                minWidth: 140
            }));
        }
        const ctrls = this.olmap.getControls();
        // Sembla que el Duration fa que no funcioni
        ctrls.forEach(element => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (element as any).duration_ = 0;
        });
        const intrs = this.olmap.getInteractions();
        intrs.forEach(element => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (element as any).duration_ = 0;
        });

        // Afegim les capes de parcel·les
        if (this.mapConfig.parceLayer && this.mapConfig.parceLayer.type === 'wms') {
            // this.parceLayer = this.createWMSLayer(this.mapConfig.parceLayer.id, this.mapConfig.parceLayer.name, this.mapConfig.parceLayer.url, { SELECT: this.mapConfig.parceLayer.layers + '.0' }, this.mapConfig.parceLayer.layers, true, true, true, 1.0, true, this.getMinZoom(this.mapConfig.parceLayer), this.getMaxZoom(this.mapConfig.parceLayer));
            this.parceLayer = this.createWMSLayer(this.mapConfig.parceLayer.id, this.mapConfig.parceLayer.name, this.mapConfig.parceLayer.url, { }, this.mapConfig.parceLayer.layers, true, true, true, 1.0, true, this.getMinZoom(this.mapConfig.parceLayer), this.getMaxZoom(this.mapConfig.parceLayer));
            this.parceLayer.setOpacity(0.5);
            this.olmap.addLayer(this.parceLayer);
        }
        if (this.mapConfig.rparceLayer && this.mapConfig.rparceLayer.type === 'wms') {
            // this.rparceLayer = this.createWMSLayer(this.mapConfig.rparceLayer.id, this.mapConfig.rparceLayer.name, this.mapConfig.rparceLayer.url, { SELECT: this.mapConfig.rparceLayer.layers + '.0' }, this.mapConfig.rparceLayer.layers, true, true, true, 1.0, true, this.getMinZoom(this.mapConfig.rparceLayer), this.getMaxZoom(this.mapConfig.rparceLayer));
            this.rparceLayer = this.createWMSLayer(this.mapConfig.rparceLayer.id, this.mapConfig.rparceLayer.name, this.mapConfig.rparceLayer.url, { }, this.mapConfig.rparceLayer.layers, true, true, true, 1.0, true, this.getMinZoom(this.mapConfig.rparceLayer), this.getMaxZoom(this.mapConfig.rparceLayer));
            this.rparceLayer.setOpacity(0.5);
            this.olmap.addLayer(this.rparceLayer);
        }

        // Afegim la capa de noms
        if (this.mapConfig.nomsLayer) {
            if (this.mapConfig.nomsLayer.type === 'wms') {
                this.nomsLayer = this.createWMSLayer(this.mapConfig.nomsLayer.id, this.mapConfig.nomsLayer.name, this.mapConfig.nomsLayer.url, this.getLayerExtraParms(this.mapConfig.nomsLayer), this.mapConfig.nomsLayer.layers, true, true, showNoms, 1.0, false, this.getMinZoom(this.mapConfig.nomsLayer), this.getMaxZoom(this.mapConfig.nomsLayer));
                this.olmap.addLayer(this.nomsLayer);
            } else if (this.mapConfig.nomsLayer.type === 'wmts' && this.mapConfig.nomsLayer.cap) {
                this.nomsLayer = this.createWMTSLayer(this.mapConfig.nomsLayer.id, this.mapConfig.nomsLayer.name, this.mapConfig.nomsLayer.cap as Options, showNoms, 1.0, this.getMinZoom(this.mapConfig.nomsLayer), this.getMaxZoom(this.mapConfig.nomsLayer));
                this.olmap.addLayer(this.nomsLayer);
            }
            this.setShowNoms(!!this.nomsLayer && showNoms);
            this.setNomsVisible(!!this.nomsLayer && showNoms);
        }

        // Afegim la capa de texts d'edificació
        if (this.mapConfig.txconsLayer) {
            if (this.mapConfig.txconsLayer.type === 'wms') {
                this.txconsLayer = this.createWMSLayer(this.mapConfig.txconsLayer.id, this.mapConfig.txconsLayer.name, this.mapConfig.txconsLayer.url, this.getLayerExtraParms(this.mapConfig.txconsLayer), this.mapConfig.txconsLayer.layers, true, true, false, 1.0, false, this.getMinZoom(this.mapConfig.txconsLayer), this.getMaxZoom(this.mapConfig.txconsLayer));
                this.olmap.addLayer(this.txconsLayer);
            } else if (this.mapConfig.txconsLayer.type === 'wmts' && this.mapConfig.txconsLayer.cap) {
                this.txconsLayer = this.createWMTSLayer(this.mapConfig.txconsLayer.id, this.mapConfig.txconsLayer.name, this.mapConfig.txconsLayer.cap as Options, false, 1.0, this.getMinZoom(this.mapConfig.txconsLayer), this.getMaxZoom(this.mapConfig.txconsLayer));
                this.olmap.addLayer(this.txconsLayer);
            }
            this.setShowTxCons(!!this.txconsLayer);
            this.setTxConsVisible(false);
        }

        // Afegim la capa de parcel·les amb llicència
        if (this.mapConfig.pllicLayer) {
            if (this.mapConfig.pllicLayer.type === 'wms') {
                this.pllicLayer = this.createWMSLayer(this.mapConfig.pllicLayer.id, this.mapConfig.pllicLayer.name, this.mapConfig.pllicLayer.url, this.getLlicParms(), this.mapConfig.pllicLayer.layers, true, true, true, 1.0, false, this.getMinZoom(this.mapConfig.pllicLayer), this.getMaxZoom(this.mapConfig.pllicLayer));
                this.pllicLayer.setOpacity(0.5);
                this.olmap.addLayer(this.pllicLayer);
            }
        }

        // Afegim la capa de marcadors
        this.addMarkerLayer();

        // Creem el tooltip d'ajuda
        this.olmap.addOverlay(this.popupOverlay);

        // Mirem si volem l'event de quan el mapa s'ha carregat
        if (this.iNumCod > 0) {
            this.olmap.once('rendercomplete', () => this.derivedSetFIDFromNumCod(this.iNumCod));
        } else if (this.iRefCad) {
            this.olmap.once('rendercomplete', () => this.derivedSetFIDFromRefCad(this.iRefCad.substring(0, 7), this.iRefCad.substring(7, 14)));
        } else if (this.iCarCod || this.iNomCarr) {
            this.olmap.once('rendercomplete', () => this.setFIDFromCarrer(this.iCarCod, this.iTipCarr, this.iNomCarr, this.iNumero));
        } else if (this.iPosX || this.iPosY) {
            this.olmap.once('rendercomplete', () => this.setInitialPos(this.iPosX, this.iPosY, this.iPosZoom)); //, this.iPosPtX, this.iPosPtY));
        }

        // Creem el tooltip d'ajuda
        if (this.mapConfig.measureTool) {
            this.helpTooltipElement.className = 'ol-tooltip hidden';
            this.olmap.addOverlay(this.helpTooltip);
        }
    }

    // Posa un QSelect del layer de parcel·les amb llicència
    public setqSelect(flt: string) {
        //console.log(flt);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const src: any = this.pllicLayer.getSource();
        if (flt) {
            if (src.params_.qfilter) {
                if (src.params_.qfilter === flt) {
                    return; // Ja teníem el mateix element seleccionat
                }
                delete src.params_.qfilter;
            }
            src.params_.qfilter = flt;
            src.refresh();

        } else {
            if (src.params_.qfilter) {
                delete src.params_.qfilter;
                src.refresh();
            }
        }
    }

    // Crea el filtre per any de registre
    private getAnyRegFilter(): string {
        if (this.filterAny.indexOf(',') > -1) {
            const anys = this.filterAny.split(',');
            let filter = '';
            anys.forEach((anyr) => {
                if (filter.length > 0) {
                    filter += ' or ';
                }
                if (anyr.indexOf('-') > -1) {
                    const anys2 = anyr.split('-');  
                    const ai = parseInt(anys2[0]);
                    const af = parseInt(anys2[1]);
                    filter += "(ANYREG >= '" + ai + "' and ANYREG <= '" + af + "')";
                } else {
                    filter += "ANYREG = '" + anyr + "'";
                }
            });
            return  '(' + filter + ')';  
        } else {
            if (this.filterAny.indexOf('-') > -1) {
                const anys = this.filterAny.split('-');
                const ai = parseInt(anys[0]);
                const af = parseInt(anys[1]);
                return "(ANYREG >= '" + ai + "' and ANYREG <= '" + af + "')";
            } else {
                return "ANYREG = '" + this.filterAny + "'";
            }
        }
    }

    // Crea el filtre per tipus de procediment
    private getClasCodFilter(): string {
        if (this.filterProc.indexOf(',') > -1) {
            const procs = this.filterProc.split(',');
            let filter = '';
            procs.forEach((proc) => {
                if (filter.length > 0) {
                    filter += ' or ';
                }
                if (proc.indexOf('.*') > -1) {
                    filter += "CLASCOD like '" + proc.replace('.*', '.%').trim() + "'";
                } else {
                    filter += "CLASCOD = '" + proc.trim() + "'";
                }
            });
            return  '(' + filter + ')';
        } else {
            if (this.filterProc.indexOf('.*') > -1) {
                return "CLASCOD like '" + this.filterProc.replace('.*', '.%').trim() + "'";
            } else {
                return "CLASCOD = '" + this.filterProc.trim() + "'";
            }
        }
    }

    // Retorna l'objecte 'parms' parametres extres per crear el layer de parcel·les amb llicència
    public getLlicParms() {
        let qfilters = "";
        qfilters = this.getClasCodFilter() + " and " + this.getAnyRegFilter();
        if (this.filterOberts && !this.filterTancats) {
            qfilters += " and TRAMITTIP <> 'T'";
        } else if (this.filterTancats && !this.filterOberts) {
            qfilters += " and TRAMITTIP = 'T'";
        }
        return { qfilter: qfilters };
    }

    // Busquem el FID d'una finca (a partir de la referència cadastral)
    public derivedSetFIDFromRefCad(finRefCad1: string, finRefCad2: string) {
        if (this.parceLayer && this.rparceLayer && finRefCad1 && finRefCad1.length === 7 && (!finRefCad2 || (finRefCad2 && finRefCad2.length === 7))) {
            const defaultParameters = {
            SERVICE: 'WFS',
            VERSION: '1.1.0',
            REQUEST: 'GetFeature',
            TYPENAME: this.mapConfig.parceLayer.layers,
            OUTPUTFORMAT: 'GML2',
            SRSNAME: 'EPSG:25831',
            FILTER: finRefCad2 ? '<Filter><And><PropertyIsEqualTo><PropertyName>FINREFCAD1</PropertyName><Literal>' + 
                                 finRefCad1 + 
                                 '</Literal></PropertyIsEqualTo><PropertyIsEqualTo><PropertyName>FINREFCAD2</PropertyName><Literal>' + 
                                 finRefCad2 + 
                                 '</Literal></PropertyIsEqualTo></And></Filter>' 
                               : '<Filter><PropertyIsEqualTo><PropertyName>FINREFCAD1</PropertyName><Literal>' + 
                                 finRefCad1 + 
                                 '</Literal></PropertyIsEqualTo></Filter>'
            };
            this.req.getRequest(this.mapConfig.wfsUrl + Util.getParamString(defaultParameters, this.mapConfig.wfsUrl)).then((resultat1) => {
                let newFID = Util.getBetween(this.mapConfig.parceLayer.layers + ' fid="', '">', resultat1 as string);
                if (newFID) {
                    this.refCadWebApi.getFinCod(finRefCad1 + finRefCad2).then((resultfc1: IFinCod) => {
                        this.numCod = 0;
                        this.finCod = resultfc1.FINCOD;
                        this.finRefCad1 = finRefCad1;
                        this.finRefCad2 = finRefCad2;
                        this.parceFid = newFID;
                        this.rparceFid = '';
                        this.setZoomPos(resultat1 as string, false);
                        this.getInfoFromPos(this.position || this.olview.getCenter());
                        this.setSelect(this.rparceLayer, '');
                        this.setSelect(this.parceLayer, newFID);
                    }).catch(() => { /* No fa res*/ });
                } else {
                    defaultParameters.TYPENAME = this.mapConfig.rparceLayer.layers;
                    this.req.getRequest(this.mapConfig.wfsUrl + Util.getParamString(defaultParameters, this.mapConfig.wfsUrl)).then((resultat2) => {
                        newFID = Util.getBetween(this.mapConfig.rparceLayer.layers + ' fid="', '">', resultat2 as string);
                        if (newFID) {
                            this.refCadWebApi.getFinCod(finRefCad1 + finRefCad2).then((resultfc2: IFinCod) => {
                                this.numCod = 0;
                                this.finCod = resultfc2.FINCOD;
                                this.finRefCad1 = finRefCad1;
                                this.finRefCad2 = finRefCad2;
                                this.parceFid = '';
                                this.rparceFid = newFID;
                                this.setZoomPos(resultat2 as string, false);
                                this.getInfoFromPos(this.position || this.olview.getCenter());
                                this.setSelect(this.parceLayer, '');
                                this.setSelect(this.rparceLayer, newFID);
                            }).catch(() => { /* No fa res*/ });
                        }
                    }).catch(() => { /* No fa res*/ });
                }
            }).catch(() => { /* No fa res*/ });
        }
    }

    // Busquem el FID d'una finca (a partir del FinCod)
    public derivedSetFIDFromFinCod(finCod: number) {
        if (this.parceLayer && this.rparceLayer && finCod) {
            this.refCadWebApi.getRefCad(finCod).then((resultrc: IRefCad) => {
                const finRefCad1: string = resultrc.FINREFCAD1;
                const finRefCad2: string = resultrc.FINREFCAD2;
                const defaultParameters = {
                    SERVICE: 'WFS',
                    VERSION: '1.1.0',
                    REQUEST: 'GetFeature',
                    TYPENAME: this.mapConfig.parceLayer.layers,
                    OUTPUTFORMAT: 'GML2',
                    SRSNAME: 'EPSG:25831',
                    FILTER: '<Filter><And><PropertyIsEqualTo><PropertyName>FINREFCAD1</PropertyName><Literal>' + 
                            finRefCad1 + 
                            '</Literal></PropertyIsEqualTo><PropertyIsEqualTo><PropertyName>FINREFCAD2</PropertyName><Literal>' + 
                            finRefCad2 + 
                            '</Literal></PropertyIsEqualTo></And></Filter>'
                };
                this.req.getRequest(this.mapConfig.wfsUrl + Util.getParamString(defaultParameters, this.mapConfig.wfsUrl)).then((resultat1) => {
                    let newFID = Util.getBetween(this.mapConfig.parceLayer.layers + ' fid="', '">', resultat1 as string);
                    if (newFID) {
                        this.numCod = 0;
                        this.finCod = finCod;
                        this.finRefCad1 = finRefCad1;
                        this.finRefCad2 = finRefCad2;
                        this.parceFid = newFID;
                        this.rparceFid = '';
                        this.setZoomPos(resultat1 as string, false);
                        this.getInfoFromPos(this.position || this.olview.getCenter());
                        this.setSelect(this.rparceLayer, '');
                        this.setSelect(this.parceLayer, newFID);
                    } else {
                        defaultParameters.TYPENAME = this.mapConfig.rparceLayer.layers;
                        this.req.getRequest(this.mapConfig.wfsUrl + Util.getParamString(defaultParameters, this.mapConfig.wfsUrl)).then((resultat2) => {
                            newFID = Util.getBetween(this.mapConfig.rparceLayer.layers + ' fid="', '">', resultat2 as string);
                            if (newFID) {
                                this.numCod = 0;
                                this.finCod = finCod;
                                this.finRefCad1 = finRefCad1;
                                this.finRefCad2 = finRefCad2;
                                this.parceFid = '';
                                this.rparceFid = newFID;
                                this.setZoomPos(resultat2 as string, false);
                                this.getInfoFromPos(this.position || this.olview.getCenter());
                                this.setSelect(this.parceLayer, '');
                                this.setSelect(this.rparceLayer, newFID);
                            }
                        }).catch(() => { /* No fa res*/ });
                    }
                }).catch(() => { /* No fa res*/ });
            }).catch(() => { /* No fa res*/ });
        }
    }

    // Busquem el FID d'una Finca (a partir del NumCod)
    public derivedSetFIDFromNumCod(numCod: number) {
        if (numCod) {
            const xml = '<?xml version="1.0" encoding="utf-8"?><soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/"><soap:Body><GetRefparByNumCod xmlns="http://www.ajmanresa.cat/GuiaWS"><pXML>' + 
            Util.encodeHTML('<WSParam><Session>' + this.ses + '</Session><Usuari>' + this.usr + '</Usuari>' + 
                            '<NumCod>' + numCod + '</NumCod></WSParam>') + '</pXML></GetRefparByNumCod></soap:Body></soap:Envelope>';
            this.req.postRequestRaw(this.mapConfig.wsGuia, xml, { headers: {'Content-Type': 'text/xml; charset=utf-8'} }).then((result) => {
                const refCad: string = Util.getBetween("<GetRefparByNumCodResult>", "</", result as string);
                if (refCad && refCad.length >= 14 ) {
                    this.derivedSetFIDFromRefCad(refCad.substring(0, 7), refCad.substring(7, 14));
                }
            }).catch(() => { /* No fa res*/ });                              
        }
    }

    // Busquem el FID d'una d'una finca (a partir d'una coordenada) (identMode = 1) o mostrem la informació urbaniística d'un punt (identMode = 2)
    public derivedSetFIDFromPos = (c: Coordinate) => {
        if (this.identMode === 1) {
            const xml = '<?xml version="1.0" ?><GetFeature version="1.1.0" service="WFS" outputFormat="GML2" xmlns:ogc="http://www.opengis.net/ogc" xmlns:gml="http://www.opengis.net/gml"><Query typeName="' + 
                        this.mapConfig.parceLayer.layers + '" srsName="EPSG:25831"><Filter><Intersects><PropertyName>GEOM</PropertyName><gml:Point gid="1" srsName="EPSG:25831"><gml:coordinates>' + 
                        c[0].toString() + ',' + c[1].toString() + '</gml:coordinates></gml:Point></Intersects></Filter></Query></GetFeature>';
            this.req.postRequestRaw(this.mapConfig.wfsUrl, xml, { headers: {'Content-Type': 'text/html;'} }).then((result) => {
                const newFID = Util.getBetween(this.mapConfig.parceLayer.layers + ' fid="', '">', result as string);
                const finRefCad1 = Util.getBetweenI('finrefcad1>', '<', result as string);
                const finRefCad2 = Util.getBetweenI('finrefcad2>', '<', result as string);
                if (newFID && finRefCad1 && finRefCad2) {
                    this.refCadWebApi.getFinCod(finRefCad1 + finRefCad2).then((resultfc1: IFinCod) => {
                        this.parceFid = newFID;
                        this.rparceFid = '';
                        this.setSelect(this.rparceLayer, '');
                        this.setSelect(this.parceLayer, newFID)
                        this.numCod = 0;
                        this.finCod = resultfc1.FINCOD;
                        this.finRefCad1 = finRefCad1;
                        this.finRefCad2 = finRefCad2;
                        this.setZoomPosCoord(c, false);
                        this.getInfoFromPos(c);
                        // this.setZoomPos(result as string, false);
                        this.identPosition = 0;
                    }).catch(() => { /* No fa res*/ });
                } else {
                    const xml = '<?xml version="1.0" ?><GetFeature version="1.1.0" service="WFS" outputFormat="GML2" xmlns:ogc="http://www.opengis.net/ogc" xmlns:gml="http://www.opengis.net/gml"><Query typeName="' + 
                                this.mapConfig.rparceLayer.layers + '" srsName="EPSG:25831"><Filter><Intersects><PropertyName>GEOM</PropertyName><gml:Point gid="1" srsName="EPSG:25831"><gml:coordinates>' + 
                                c[0] + ',' + c[1] + '</gml:coordinates></gml:Point></Intersects></Filter></Query></GetFeature>';
                    this.req.postRequestRaw(this.mapConfig.wfsUrl, xml, { headers: {'Content-Type': 'text/html;'} }).then((result) => {
                        const newFID = Util.getBetween(this.mapConfig.rparceLayer.layers + ' fid="', '">', result as string);
                        const finRefCad1 = Util.getBetweenI('finrefcad1>', '<', result as string);
                        const finRefCad2 = Util.getBetweenI('finrefcad2>', '<', result as string);
                        if (newFID && finRefCad1 && finRefCad2) {
                            this.refCadWebApi.getFinCod(finRefCad1 + finRefCad2).then((resultfc1: IFinCod) => {
                                this.parceFid = '';
                                this.rparceFid = newFID;
                                this.setSelect(this.parceLayer, '');
                                this.setSelect(this.rparceLayer, newFID)
                                this.numCod = 0;
                                this.finCod = resultfc1.FINCOD;
                                this.finRefCad1 = finRefCad1;
                                this.finRefCad2 = finRefCad2;
                                this.setZoomPosCoord(c, false);
                                this.getInfoFromPos(c);
                                // this.setZoomPos(result as string, false);
                                this.identPosition = 0;
                            }).catch(() => { /* No fa res*/ });
                        } else { // Guardem la posició punxida sense guardar adreça ni referència
                            this.parceFid = '';
                            this.rparceFid = '';
                            this.setSelect(this.parceLayer, '');
                            this.setSelect(this.rparceLayer, '');
                            this.finCod = 0;
                            this.finRefCad1 = '';
                            this.finRefCad2 = '';
                            this.numCod = 0;
                            this.setZoomPosCoord(c, false);
                            this.identPosition = 0;
                        }
                    }).catch(() => { /* No fa res*/ }); 
                }
            }).catch(() => { /* No fa res*/ }); 
        }
    }

    // Llegim la informació urbanística d'un punt
    private getInfoFromPos = (c: Coordinate) => {
        if (this.finRefCad1 && this.finRefCad2) {
            const xml = Util.getXMLRequest('LlicenciesPerRefCad', ['Session', 'Usuari', 'RefCad'], [this.ses, this.usr, this.finRefCad1 + this.finRefCad2]);
            this.req.postRequestRaw(this.mapConfig.wsSig, xml, { headers: {'Content-Type': 'text/xml;'} }).then((result) => { /* No fa res */
                this.setPoupContent(c, Util.getXMLResponse('LlicenciesPerRefCad', result as string));
            }).catch(() => { /* No fa res*/ }); 
        }
    }
    
    // Llegim un element a partir d'una coordenada
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public derivedGetElementFromPos = (c: Coordinate, iLy?: number) => {
        // En les llicències aquesta funció no fa res
        return;
    }

    // Busquem el FID d'un element element a partir d'una coordenada si el trobem mostrem la foto, si no mostrem la foto de la façana
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public derivedGetFIDFromPosPhotoElem = (c: Coordinate, iLy?: number): Promise<string | undefined> => {
        // En les llicències aquesta funció no fa res
        return undefined;
    }

    // Posa o treu el checkbox dels texts d'edificació
    @action setShowTxCons(show: boolean) {
        this.showTxCons = show;
    }
 
    // Posa o treu el checkbox dels texts d'edificació
    @action setTxConsVisible(show: boolean) {
        this.txConsVisible = show;
    }

    // Que fem al clicar un punt del mapa (1 -> Triem parcel·la)
    @action setIdentMode(identMode: number) {
        this.identMode = identMode;
    }

    // Crida al WS per crear el registre de log
    public creaLog(ref: string, func: string) {
        const xml = '<?xml version="1.0" encoding="utf-8"?><soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/"><soap:Body><CreaLog xmlns="http://www.ajmanresa.cat/WSSig"><pXML>' + 
                    Util.escapeXML('<WSParam><Session /><Aplicacio>FitxUrb17</Aplicacio><Funcio>' + func + '</Funcio><Params>' + ref + '</Params></WSParam>') +
                    '</pXML></CreaLog></soap:Body></soap:Envelope>';
        this.req.postRequestRaw(this.mapConfig.wsSig, xml, { headers: {'Content-Type': 'text/xml;'} }).then(() => { /* No fa res */}).catch(() => { /* No fa res*/ }); 
    }

    /** Posa el filtre de procediment
     * @param flt proc
     */
    @action setFilterProc(flt: string) {
        this.filterProc = flt;
    }
   
    /** Posa el filtre d'any de registre del procediment
     * @param flt any
     */
    @action setFilterAny(flt: string) {
        this.filterAny = flt;
    }
   
    /** Posa el filtre de procediments oberts
     * @param flt any
     */
    @action setFilterOberts(flt: boolean) {
        this.filterOberts = flt;
        if (!flt && !this.filterTancats) {
            this.filterTancats = true
        }
    }
   
    /** Posa el filtre de procediments tancats
     * @param flt any
     */
    @action setFilterTancats(flt: boolean) {
        this.filterTancats = flt;
        if (!flt && !this.filterOberts) {
            this.filterOberts = true
        }
    }
       
}
